import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import { useBannerDismissal } from "../../hooks/useBannerDismissal";

const MUSIC_SHOWCASE_URL = "https://outschool.com/campaigns/music-showcase";
const BANNER_NAME = "music_showcase";
const BANNER_START_DATE = "2025-11-03";
const BANNER_END_DATE = "2025-12-01";

export function IsValidTimeRangeForMusicShowcaseBanner() {
  const now = dayjs();
  const startDate = dayjs(BANNER_START_DATE);
  const endDate = dayjs(BANNER_END_DATE);

  return now.isSameOrAfter(startDate, "day") && now.isBefore(endDate, "day");
}

export default function MusicShowcaseBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\MusicShowcaseBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const { dismissBanner, isBannerDismissed } = useBannerDismissal();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      trackEvent("music_showcase_banner_view", {
        is_mobile: isMobile,
      });
    }
  }, [trackEvent, isMobile]);

  const handleBannerClick = () => {
    trackEvent("music_showcase_banner_click", {
      is_mobile: isMobile,
    });
    navigate(MUSIC_SHOWCASE_URL, {
      hardNav: true,
      newTab: true,
    });
  };

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackEvent("music_showcase_banner_dismiss", {
      is_mobile: isMobile,
    });
    dismissBanner(BANNER_NAME);
  };

  if (
    !IsValidTimeRangeForMusicShowcaseBanner() ||
    isBannerDismissed(BANNER_NAME)
  ) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              <Trans t={t}>
                🎵 Showcase your kid’s musical talent and win up to $1,000 in
                prize value!
              </Trans>
              <Box
                component="span"
                sx={{
                  marginLeft: 8,
                  textDecoration: "underline",
                }}
              >
                {t("Enter Now")}
              </Box>
            </Typography>
          </Box>
        </Box>
        <Icon
          icon={faTimes}
          onClick={handleDismiss}
          aria-label={t("Close banner")}
          sx={(theme: Theme) => ({
            color: theme.palette.common.white,
            position: "absolute",
            right: 0,
            width: "2rem",
            height: "2rem",
            cursor: "pointer",
          })}
        />
      </Box>
    </Box>
  );
}
